import React, { PureComponent } from "react";
import Fragment from 'react-dot-fragment';
import styled from 'styled-components';
import { Link } from "react-router";

import { fetchFinancialStatements } from '../../../../api/FinancialStatementsService';
import { fetchAllStaticData } from '../../../../api/StaticDataService';

import OneVisualLayout from "../../layouts/one-visual";

const FeedbackBlock = styled.div`
  width: 100%;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #d5d9dd;
  padding: 20px;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.6);
  margin-bottom: 20px;
`;

const FeedbackBlockTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  line-height: 16px;
  color: rgba(0, 27, 59, 0.94);
  margin-bottom: 7px;
`;

const FeedbackBlockList = styled.ul`
  line-height: 24px;
`;

const FeedbackBlockListItem = styled.li`
  line-height: 24px;
`;

class FeedbackOverall extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      incomeStatement: {},
      staticConstants: {}
    };
  }

  componentDidMount() {
    const { activeRound } = this.props;

    Promise.all([
      fetchFinancialStatements({ roundNumber: activeRound }),
      // performance-feedback scope returns constants as an object
      // (not a single-element array), which is what staticConstants
      // was already treated as. Previously the un-scoped payload
      // returned constants as an array here, so this component's
      // `staticConstants.holding_rate` read was quietly always
      // undefined; switching to the scope also fixes that.
      fetchAllStaticData(activeRound, 'performance-feedback'),
    ])
      .then(([financialData, staticData]) => {
        const incomeStatement = financialData.incomeStatement[0] || {};
        const staticConstants = staticData.constants || {};

        this.setState({
          incomeStatement,
          staticConstants
        });
      })
      .catch(() => { });
  }

  render() {
    const { reportsPerformanceFeedback, resultsTargets, activeRound } = this.props;
    const { incomeStatement, staticConstants } = this.state;

    const hasDidNotSubmitOrInsufficientCapital = reportsPerformanceFeedback.some(item =>
      item.type === "Did not submit" || item.type === "Insufficient capital"
    );

    const submissionStatusText = (incomeStatement['Net Revenues'] === 0 && hasDidNotSubmitOrInsufficientCapital) ? 'Some team decisions weren’t posted, either because they weren’t made or your team didn’t have sufficient capital. Check the CEO page for unposted decisions and/or the Finance page for your cash balance.'
      : 'Your team posted all decisions successfully for this round.';

    const hasMarketFitIssues = reportsPerformanceFeedback.some(item =>
      item.type === "Product market fit" || item.type === "Price range failed"
    );

    const hasPriceSupplyIssues = reportsPerformanceFeedback.some(item => item.type === "Expected revenue low");

    const hasAwarenessIssues = reportsPerformanceFeedback.some(item => item.type === "Awareness");

    const hasMarketShareNetRevenuesTargets = resultsTargets.net_revenues !== 0 || resultsTargets.market_share !== 0;
    const hasMarketShareOrRevenueFail = reportsPerformanceFeedback.some(item =>
      item.type === "Market share" || item.type === "Revenue fail"
    );
    const hasNoRevenueIssues = !reportsPerformanceFeedback.some(item =>
      item.type === "Product market fit" ||
      item.type === "Price range failed" ||
      item.type === "Expected revenue low" ||
      item.type === "Awareness"
    );

    const hasNegativeGrossMargins = reportsPerformanceFeedback.some(item => item.type === "Negative gross margins");

    const hasGrossMarginFail = reportsPerformanceFeedback.some(item => item.type === "Gross margin fail");

    const hasAutoOrMobileOverproduced = reportsPerformanceFeedback.some(item =>
      item.type === "Auto overproduced" || item.type === "Mobile overproduced"
    );

    const hasHighInterest = reportsPerformanceFeedback.some(item => item.type === "High interest");

    const hasRoeRoaEps = resultsTargets.roe !== 0 || resultsTargets.roa !== 0 || resultsTargets.earnings_per_share !== 0;

    const hasRoeRoaEpsFail = reportsPerformanceFeedback.some(item =>
      item.type === "Roe fail" || item.type === "Roa fail" || item.type === "EPS fail"
    );

    const hasNoGrossMarginIssuesOrOverproducedOrHighInterest = !reportsPerformanceFeedback.some(item =>
      item.type === "Negative gross margins" ||
      item.type === "Gross margin fail" ||
      item.type === "Auto overproduced" ||
      item.type === "Mobile overproduced" ||
      item.type === "High interest"
    );

    // Whether each feedback block will render any <li>. Used to
    // fall back to a "No feedback." placeholder when the team hit
    // its targets and none of the gating flags are set.
    const showMarketShareRevenueMix =
      hasMarketShareNetRevenuesTargets
      && hasMarketShareOrRevenueFail
      && hasNoRevenueIssues;
    const hasMarketShareRevenuesFeedback =
      hasMarketFitIssues
      || hasPriceSupplyIssues
      || hasAwarenessIssues
      || showMarketShareRevenueMix;

    const showProfitChallenges =
      hasRoeRoaEps
      && hasRoeRoaEpsFail
      && hasNoGrossMarginIssuesOrOverproducedOrHighInterest;
    const hasProfitsFeedback =
      hasNegativeGrossMargins
      || hasGrossMarginFail
      || hasAutoOrMobileOverproduced
      || hasHighInterest
      || showProfitChallenges;

    return (
      <OneVisualLayout
        section="Feedback | OVERALL"
        title="Overall Feedback"
        paragraph1={(
          <Fragment>
            <FeedbackBlock>
              <FeedbackBlockTitle>Submission Status</FeedbackBlockTitle>
              <p>{submissionStatusText}</p>
            </FeedbackBlock>
            <FeedbackBlock>
              <FeedbackBlockTitle>Feedback: Generating Market Share & Revenues</FeedbackBlockTitle>
              {hasMarketShareRevenuesFeedback ? (
                <FeedbackBlockList>
                  {hasMarketFitIssues && (
                    <FeedbackBlockListItem>
                      <span>Market Fit Challenges:</span> Your team has a product that is either targeting customers with low demand or is priced outside what the target market is willing to pay. Review customer analysis to better understand customer preferences.
                    </FeedbackBlockListItem>
                  )}

                  {hasPriceSupplyIssues && (
                    <FeedbackBlockListItem>
                      <span>Price & Supply Misalignment:</span> You either produced or set your product’s price too low given customer demand and what they were willing to pay. There might be coordination issues between your operations function (units available) and your marketing function (selling price).
                    </FeedbackBlockListItem>
                  )}

                  {hasAwarenessIssues && (
                    <FeedbackBlockListItem>
                      <span>Awareness:</span> Visit the Awareness page (<Link to={`/analysis/team-reports/marketing-analysis/marketing-awareness?round=${activeRound}`}>click here</Link>) to see how your product’s awareness compares to competitors. If it’s relatively low, consider increasing your promotional spend.
                    </FeedbackBlockListItem>
                  )}

                  {showMarketShareRevenueMix && (
                    <FeedbackBlockListItem>
                      <span>Market Mix Challenges:</span> Your current 4Ps may be hindering performance. Review the Marketing Analysis in your Team Report to compare your product’s features, pricing, and awareness with top competitors.
                    </FeedbackBlockListItem>
                  )}
                </FeedbackBlockList>
              ) : (
                <p>No feedback.</p>
              )}
            </FeedbackBlock>
            <FeedbackBlock>
              <FeedbackBlockTitle>Feedback on Generating Profits</FeedbackBlockTitle>
              {hasProfitsFeedback ? (
                <FeedbackBlockList>
                  {hasNegativeGrossMargins && (
                    <FeedbackBlockListItem>
                      <span>Negative Margins:</span> It looks like your product’s selling price is below its production cost. Remember, this isn’t a nonprofit game. Ensure Marketing (who sets price) and Operations (who manages cost) are coordinating.
                    </FeedbackBlockListItem>
                  )}

                  {hasGrossMarginFail && (
                    <FeedbackBlockListItem>
                      <span>Low Gross Margins:</span> Your team’s margins are insufficient. Review your product’s selling price and manufacturing cost, and adjust as needed.
                    </FeedbackBlockListItem>
                  )}

                  {hasAutoOrMobileOverproduced && (
                    <FeedbackBlockListItem>
                      <span>Overproduced:</span> Overproducing creates unsold inventory, carrying holding costs of about {staticConstants.holding_rate && staticConstants.holding_rate.toLocaleString('en-US', { style: 'percent' })} of its value. Remember, a top product in a segment (e.g., mobile) can capture roughly 15% of the market; producing beyond that may result in excess inventory.
                    </FeedbackBlockListItem>
                  )}

                  {hasHighInterest && (
                    <FeedbackBlockListItem>
                      <span>High Interest:</span> Your outstanding loan is incurring a lot of interest, which impacts your profitability. Monitor it closely and consider paying down your loan or interest payable.
                    </FeedbackBlockListItem>
                  )}

                  {showProfitChallenges && (
                    <FeedbackBlockListItem>
                      <span>Profit Challenges:</span> Your revenues aren't keeping up with expenses, which can lower your score in rounds targeting profit metrics (ROE, ROA, EPS). Check your income statement in Team Reports to see what’s impacting profitability.
                    </FeedbackBlockListItem>
                  )}
                </FeedbackBlockList>
              ) : (
                <p>No feedback.</p>
              )}
            </FeedbackBlock>
          </Fragment>
        )}
        visualProps={{
          noBorder: true,
          noBackground: true,
        }}
      />
    );
  }
}

export default FeedbackOverall;



// WEBPACK FOOTER //
// ./src/components/performance-feedback/pages/feedback/index.js